import Phaser from 'phaser';
import { Preferences } from '../WebPreferences';
import audioManager from '../AudioManager';
import { getAdsRemoved, purchaseRemoveAds, purchaseLifetime, getProductPrice, getLifetimePrice } from '../IAPManager';
import { showBanner, hideBanner, showRewardedCoin, BANNER_HEIGHT } from '../AdManager';
import { initRating, markAsked, markNeverAsk, openPlayStoreListing } from '../RatingManager';
import { getCoins, spendCoins, canWatchAdForCoins, recordAdWatch, getAdWatchesRemaining, COINS_PER_AD, MAX_ADS_PER_DAY } from '../CoinManager';
import { getUnlockedAnimals, unlockAnimal, UNLOCKABLE_ANIMALS } from '../AnimalShopManager';

// ─────────────────────────────────────────────────────────────────────────────
// HomeScene — procedurally animated sky/ground background with roaming animals.
// Original homescreen.png is still loaded as key 'homescreen' (unused by default
// but available if you want to revert by swapping the background block).
// ─────────────────────────────────────────────────────────────────────────────
export default class HomeScene extends Phaser.Scene {
  constructor() {
    super({ key: 'HomeScene' });
    this.scoreText          = null;
    this.settingsContainer  = null;
    this.removeAdsContainer = null;
    this.ratingContainer    = null;
    this.coinContainer      = null;
    this.coinCounterText    = null;
    this.shopContainer      = null;
  }

  preload() {
    this.load.image('homescreen', 'assets/images/homescreen.png'); // kept as fallback
    // Animals (may already be cached from SplashScene — safe to re-declare)
    this.load.image('bear',      'assets/images/bear.png');
    this.load.image('elephant',  'assets/images/elephant.png');
    this.load.image('giraffe',   'assets/images/giraffe.png');
    this.load.image('lion',      'assets/images/lion.png');
    this.load.image('tiger',     'assets/images/tiger.png');
    this.load.image('rhino',     'assets/images/rhino.png');
    this.load.image('cat',       'assets/images/cat.png');
    this.load.image('fox',       'assets/images/fox.png');
    this.load.image('eagle',     'assets/images/eagle.png');
    this.load.image('cow',       'assets/images/cow.png');
    this.load.image('panda',     'assets/images/panda.png');
    this.load.image('pig',       'assets/images/pig.png');
    this.load.image('puppy',     'assets/images/puppy.png');
    this.load.image('horse',     'assets/images/horse.png');
    this.load.image('tiger2',    'assets/images/tiger2.png');

    // Ensure Chewy is loaded before Phaser renders the title text
    this._chewyReady = false;
    document.fonts.load('1em Chewy').then(() => {
      this._chewyReady = true;
    }).catch(() => {
      this._chewyReady = true;
    });
  }

  create(data) {
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    // ════════════════════════════════════════════════════════════
    //  BACKGROUND
    // ════════════════════════════════════════════════════════════

    // ── Sky gradient ──────────────────────────────────────────────
    const sky = this.add.graphics().setDepth(0);
    sky.fillGradientStyle(0x29b6f6, 0x29b6f6, 0xb3e5fc, 0xb3e5fc, 1);
    sky.fillRect(0, 0, W, H);

    // ── Sun ───────────────────────────────────────────────────────
    const sunX = W * 0.82, sunY = H * 0.10, sunR = 32 * sd;
    const sunGfx = this.add.graphics().setDepth(0);
    for (let i = 4; i >= 1; i--) {
      sunGfx.fillStyle(0xffe066, 0.05 * i);
      sunGfx.fillCircle(sunX, sunY, sunR + i * 13 * sd);
    }
    sunGfx.fillStyle(0xfdd835, 1);
    sunGfx.fillCircle(sunX, sunY, sunR);
    sunGfx.fillStyle(0xffee58, 1);
    sunGfx.fillCircle(sunX - sunR * 0.22, sunY - sunR * 0.22, sunR * 0.40);

    // ── Clouds (animated drift) ───────────────────────────────────
    const cloudDefs = [
      { x: W * 0.14, y: H * 0.08, s: 1.0, speed: 3400 },
      { x: W * 0.50, y: H * 0.05, s: 0.72, speed: 4000 },
      { x: W * 0.80, y: H * 0.16, s: 0.88, speed: 3700 },
      { x: W * 0.34, y: H * 0.19, s: 0.62, speed: 4500 },
    ];
    cloudDefs.forEach(c => {
      const g = this._makeCloud(c.x, c.y, c.s * sd, 0);
      this.tweens.add({
        targets: g, x: `+=${(18 + c.s * 10) * sd}`,
        duration: c.speed, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });

    // ── Distant rolling hills ─────────────────────────────────────
    const hillGfx = this.add.graphics().setDepth(0);
    hillGfx.fillStyle(0x81c784, 0.50);
    hillGfx.fillEllipse(W * 0.10, H * 0.72, W * 0.56, H * 0.26);
    hillGfx.fillEllipse(W * 0.80, H * 0.70, W * 0.60, H * 0.24);
    hillGfx.fillStyle(0x66bb6a, 0.40);
    hillGfx.fillEllipse(W * 0.46, H * 0.74, W * 0.50, H * 0.20);

    // ── Ground ────────────────────────────────────────────────────
    const groundY = H * 0.76;
    const gndGfx  = this.add.graphics().setDepth(0);
    gndGfx.fillGradientStyle(0x43a047, 0x43a047, 0x2e7d32, 0x2e7d32, 1);
    gndGfx.fillRect(0, groundY, W, H - groundY);
    // Grass tufts
    gndGfx.fillStyle(0x66bb6a, 1);
    for (let x = 4 * sd; x < W; x += 18 * sd) {
      const h = (4 + Math.sin(x * 0.04) * 2.5) * sd;
      gndGfx.fillTriangle(x, groundY, x + 7 * sd, groundY, x + 3.5 * sd, groundY - h);
    }

    // ── Decorative tall tower stack (left side) ───────────────────
    const stackKeys = ['elephant', 'rhino', 'bear', 'giraffe'];
    const blockSize = 44 * sd;
    const towerBaseY = groundY - 4 * sd;
    const towerX     = W * 0.12;
    stackKeys.forEach((key, i) => {
      const y = towerBaseY - i * blockSize - blockSize / 2;
      const img = this.add.image(towerX, y, key)
        .setDisplaySize(blockSize, blockSize)
        .setDepth(0)
        .setAlpha(0);
      this.tweens.add({
        targets: img, alpha: 0.85, y,
        duration: 350, delay: 200 + i * 150, ease: 'Back.easeOut',
      });
      // Gentle sway
      this.tweens.add({
        targets: img, angle: (i % 2 === 0 ? 2 : -2),
        duration: 1800 + i * 200, yoyo: true, repeat: -1,
        delay: 800 + i * 150, ease: 'Sine.easeInOut',
      });
    });

    // ── Roaming animals (right side, walking) ─────────────────────
    const roamers = [
      { key: 'cat',   baseX: W * 0.78, y: groundY - 16 * sd, size: 30 * sd, range: 22 * sd, dur: 1800 },
      { key: 'fox',   baseX: W * 0.88, y: groundY - 14 * sd, size: 26 * sd, range: 16 * sd, dur: 2200 },
      { key: 'tiger', baseX: W * 0.70, y: groundY - 18 * sd, size: 32 * sd, range: 18 * sd, dur: 2600, flip: true },
    ];
    roamers.forEach(r => {
      const img = this.add.image(r.baseX, r.y, r.key)
        .setDisplaySize(r.size, r.size)
        .setDepth(0)
        .setAlpha(0)
        .setFlipX(r.flip || false);
      this.tweens.add({ targets: img, alpha: 0.9, duration: 600, delay: 400, ease: 'Power2' });
      this.tweens.add({
        targets: img, x: r.baseX + r.range,
        duration: r.dur, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
        onYoyo: () => img.setFlipX(!img.flipX),
        onRepeat: () => img.setFlipX(!img.flipX),
      });
      // Bob
      this.tweens.add({
        targets: img, y: r.y - 3 * sd,
        duration: r.dur / 4, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });

    // ── Eagle flies across once every few seconds ─────────────────
    const spawnEagle = () => {
      if (!this.scene.isActive('HomeScene')) return;
      const eagleY  = Phaser.Math.Between(H * 0.08, H * 0.30);
      const fromLeft = Phaser.Math.Between(0, 1) === 0;
      const startX  = fromLeft ? -55 * sd : W + 55 * sd;
      const endX    = fromLeft ? W + 55 * sd : -55 * sd;
      const e = this.add.image(startX, eagleY, 'eagle')
        .setDisplaySize(44 * sd, 44 * sd)
        .setDepth(1)
        .setAlpha(0.85)
        .setFlipX(!fromLeft);
      this.tweens.add({ targets: e, scaleY: `*=0.82`, duration: 200, yoyo: true, repeat: -1 });
      this.tweens.add({
        targets: e, x: endX, duration: 3200, ease: 'Power1',
        onComplete: () => {
          e.destroy();
          this.time.delayedCall(Phaser.Math.Between(5000, 9000), spawnEagle);
        },
      });
    };
    this.time.delayedCall(1500, spawnEagle);

    // ════════════════════════════════════════════════════════════
    //  COIN COUNTER (top-left)
    // ════════════════════════════════════════════════════════════
    const coinY = 48 * sd;
    const coinX = 20 * sd;
    const coinIconSize = 22 * sd;
    const coinPillW = 110 * sd;
    const coinPillH = 36 * sd;

    const coinBg = this.add.graphics().setDepth(5);
    coinBg.fillStyle(0x000000, 0.45);
    coinBg.fillRoundedRect(coinX, coinY - coinPillH / 2, coinPillW, coinPillH, coinPillH / 2);

    // Coin icon
    const coinIconGfx = this.add.graphics().setDepth(6);
    const cix = coinX + coinPillH / 2;
    coinIconGfx.fillStyle(0xddaa00, 1);
    coinIconGfx.fillCircle(cix, coinY, coinIconSize * 0.5);
    coinIconGfx.fillStyle(0xffdd44, 1);
    coinIconGfx.fillCircle(cix, coinY, coinIconSize * 0.38);
    coinIconGfx.fillStyle(0xddaa00, 1);
    coinIconGfx.fillCircle(cix, coinY, coinIconSize * 0.15);

    // Coin count text
    this.coinCounterText = this.add.text(coinX + coinPillH + 8 * sd, coinY, '0', {
      fontSize: `${16 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0, 0.5).setDepth(6);

    // Plus icon button
    const plusSize = 28 * sd;
    const plusX = coinX + coinPillW + 6 * sd;
    const plusGfx = this.add.graphics().setDepth(6);
    const drawPlus = (color) => {
      plusGfx.clear();
      plusGfx.fillStyle(color, 1);
      plusGfx.fillCircle(plusX + plusSize / 2, coinY, plusSize / 2);
      plusGfx.fillStyle(0xffffff, 1);
      const bar = 3 * sd;
      const len = 12 * sd;
      plusGfx.fillRect(plusX + plusSize / 2 - len / 2, coinY - bar / 2, len, bar);
      plusGfx.fillRect(plusX + plusSize / 2 - bar / 2, coinY - len / 2, bar, len);
    };
    drawPlus(0x44bb44);

    // Hit area for coin counter + plus button
    const coinHitW = coinPillW + plusSize + 10 * sd;
    const coinHit = this.add.rectangle(coinX + coinHitW / 2, coinY, coinHitW, coinPillH)
      .setInteractive({ useHandCursor: true }).setDepth(6).setAlpha(0.001);

    coinHit.on('pointerdown', () => {
      this.showCoinBanner(sd, dpr);
    });

    getCoins().then(coins => {
      if (this.coinCounterText) this.coinCounterText.setText(`${coins}`);
    });

    // ════════════════════════════════════════════════════════════
    //  GAME TITLE — spawned after Chewy font confirmed ready
    // ════════════════════════════════════════════════════════════
    const line1Y = H * 0.33;
    const line2Y = line1Y + 42 * sd;

    const spawnTitle = () => {
      this.add.text(W / 2 + 3 * sd, line1Y + 4 * sd, 'Animal', {
        fontSize: `${40 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#1a4a1a',
      }).setOrigin(0.5).setDepth(1);
      this.add.text(W / 2 + 3 * sd, line2Y + 4 * sd, 'Stack', {
        fontSize: `${40 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#1a4a1a',
      }).setOrigin(0.5).setDepth(1);

      const titleLine1 = this.add.text(W / 2, line1Y, 'Animal', {
        fontSize: `${40 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ffffff', stroke: '#2e7d32', strokeThickness: 7 * dpr,
      }).setOrigin(0.5).setDepth(1);
      const titleLine2 = this.add.text(W / 2, line2Y, 'Stack', {
        fontSize: `${40 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: '#ffffff', stroke: '#2e7d32', strokeThickness: 7 * dpr,
      }).setOrigin(0.5).setDepth(1);

      this.tweens.add({
        targets: [titleLine1, titleLine2], y: `-=${5 * sd}`,
        duration: 1600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    };

    const waitForFont = () => {
      if (this._chewyReady) { spawnTitle(); }
      else { this.time.delayedCall(50, waitForFont); }
    };
    waitForFont();

    // ════════════════════════════════════════════════════════════
    //  BUTTONS  (all logic identical to original)
    // ════════════════════════════════════════════════════════════

    // ── Play buttons (PLAY + VS BOT side by side) ─────────────────
    const totalBtnW = Math.min(320 * sd, W * 0.82);
    const btnGap    = 10 * sd;
    const btnW      = (totalBtnW - btnGap) / 2;
    const btnH      = 80 * sd;
    const btnY      = H * 0.82;
    const radius    = btnH / 2;
    const playBtnX  = W / 2 - btnGap / 2 - btnW / 2;
    const botBtnX   = W / 2 + btnGap / 2 + btnW / 2;

    // ── PLAY button ───────────────────────────────────────────────
    const btnGfx = this.add.graphics().setDepth(2);
    const drawBtn = (color) => {
      btnGfx.clear();
      btnGfx.fillStyle(color, 1);
      btnGfx.fillRoundedRect(playBtnX - btnW / 2, btnY - btnH / 2, btnW, btnH, radius);
    };
    drawBtn(0xff6600);

    const hitArea = this.add.rectangle(playBtnX, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true }).setDepth(2);
    const btnText = this.add.text(playBtnX, btnY, '▶  PLAY', {
      fontSize: `${22 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 5 * dpr,
    }).setOrigin(0.5).setDepth(3);

    hitArea.on('pointerover',  () => drawBtn(0xff8800));
    hitArea.on('pointerout',   () => drawBtn(0xff6600));
    hitArea.on('pointerdown',  () => {
      drawBtn(0xcc4400);
      audioManager.ensureStarted();
      this.tweens.add({ targets: [btnGfx, btnText], scaleX: 0.95, scaleY: 0.95, duration: 80, yoyo: true });
    });
    hitArea.on('pointerup', () => {
      drawBtn(0xff6600);
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('GameScene', { mode: 'solo' });
      });
    });

    // ── VS BOT button ─────────────────────────────────────────────
    const botGfx = this.add.graphics().setDepth(2);
    const drawBotBtn = (color) => {
      botGfx.clear();
      botGfx.fillStyle(color, 1);
      botGfx.fillRoundedRect(botBtnX - btnW / 2, btnY - btnH / 2, btnW, btnH, radius);
    };
    drawBotBtn(0x6644cc);

    const botHitArea = this.add.rectangle(botBtnX, btnY, btnW, btnH)
      .setInteractive({ useHandCursor: true }).setDepth(2);
    const botBtnText = this.add.text(botBtnX, btnY, '\u{1F916}  VS BOT', {
      fontSize: `${22 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 5 * dpr,
    }).setOrigin(0.5).setDepth(3);

    botHitArea.on('pointerover',  () => drawBotBtn(0x8866ee));
    botHitArea.on('pointerout',   () => drawBotBtn(0x6644cc));
    botHitArea.on('pointerdown',  () => {
      drawBotBtn(0x442299);
      audioManager.ensureStarted();
      this.tweens.add({ targets: [botGfx, botBtnText], scaleX: 0.95, scaleY: 0.95, duration: 80, yoyo: true });
    });
    botHitArea.on('pointerup', () => {
      drawBotBtn(0x6644cc);
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('GameScene', { mode: 'vs_bot' });
      });
    });

    // ── Leaderboard button ────────────────────────────────────────
    const lbBtnW = Math.min(totalBtnW, W * 0.55);
    const lbBtnH = 52 * sd;
    const lbBtnY = btnY - btnH * 0.5 - lbBtnH * 0.5 - 16 * sd;
    const lbBtnR = lbBtnH / 2;

    const lbGfx = this.add.graphics().setDepth(2);
    const drawLbBtn = (color) => {
      lbGfx.clear();
      lbGfx.fillStyle(color, 1);
      lbGfx.fillRoundedRect(W / 2 - lbBtnW / 2, lbBtnY - lbBtnH / 2, lbBtnW, lbBtnH, lbBtnR);
    };
    drawLbBtn(0x1a5fa8);

    this.add.text(W / 2, lbBtnY, '\u{1F3C6}  Leaderboard', {
      fontSize: `${16 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(3);

    const lbHit = this.add.rectangle(W / 2, lbBtnY, lbBtnW, lbBtnH)
      .setInteractive({ useHandCursor: true }).setDepth(3);
    lbHit.on('pointerover',  () => drawLbBtn(0x2277cc));
    lbHit.on('pointerout',   () => drawLbBtn(0x1a5fa8));
    lbHit.on('pointerdown',  () => {
      drawLbBtn(0x0e3d6e);
      this.cameras.main.fadeOut(250, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HomeScene');
        this.scene.start('LeaderboardScene');
      });
    });

    // ── Remove Ads button (above leaderboard button) ──────────────
    const removeAdsBtnW = Math.min(totalBtnW, W * 0.55);
    const removeAdsBtnH = 52 * sd;
    const removeAdsBtnY = lbBtnY - lbBtnH * 0.5 - removeAdsBtnH * 0.5 - 10 * sd;
    const removeAdsBtnR = removeAdsBtnH / 2;

    const removeAdsGfx = this.add.graphics().setDepth(2);
    const drawRemoveAdsBtn = (color) => {
      removeAdsGfx.clear();
      removeAdsGfx.fillStyle(color, 1);
      removeAdsGfx.fillRoundedRect(
        W / 2 - removeAdsBtnW / 2, removeAdsBtnY - removeAdsBtnH / 2,
        removeAdsBtnW, removeAdsBtnH, removeAdsBtnR
      );
    };

    const removeAdsBtnText = this.add.text(W / 2, removeAdsBtnY, '', {
      fontSize: `${16 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(3);

    const removeAdsHit = this.add.rectangle(W / 2, removeAdsBtnY, removeAdsBtnW, removeAdsBtnH)
      .setDepth(3);

    const applyRemoveAdsState = (adsRemoved) => {
      if (adsRemoved) {
        drawRemoveAdsBtn(0x448844);
        removeAdsBtnText.setText('✓  Ads Removed');
        removeAdsHit.removeInteractive();
      } else {
        drawRemoveAdsBtn(0xddaa00);
        removeAdsBtnText.setText('⭐  Remove Ads');
        removeAdsHit.setInteractive({ useHandCursor: true });
        removeAdsHit.on('pointerover',  () => drawRemoveAdsBtn(0xffcc22));
        removeAdsHit.on('pointerout',   () => drawRemoveAdsBtn(0xddaa00));
        removeAdsHit.on('pointerdown',  () => {
          this.showRemoveAdsCard(sd, dpr, applyRemoveAdsState);
        });
      }
    };
    getAdsRemoved().then(applyRemoveAdsState);

    // ── Animal Shop button (above settings gear) ───────────────
    const shopSize = 48 * sd;
    const shopX    = W - shopSize / 2 - 16 * sd;
    const shopY    = Math.max(shopSize / 2 + 10 * sd, removeAdsBtnY - shopSize - 10 * sd);

    const shopGfx = this.add.graphics().setDepth(3);
    const drawShop = (color) => {
      shopGfx.clear();
      shopGfx.fillStyle(color, 0.85);
      shopGfx.fillRoundedRect(shopX - shopSize / 2, shopY - shopSize / 2, shopSize, shopSize, shopSize * 0.25);
    };
    drawShop(0x44663a);

    this.add.text(shopX, shopY, '\u{1F43E}', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(4);

    const shopHit = this.add.rectangle(shopX, shopY, shopSize, shopSize)
      .setInteractive({ useHandCursor: true }).setDepth(4);
    shopHit.on('pointerover',  () => drawShop(0x558844));
    shopHit.on('pointerout',   () => drawShop(0x44663a));
    shopHit.on('pointerdown',  () => { drawShop(0x334422); this.showAnimalShop(sd, dpr); });

    // ── Settings button ────────────────────────────────────────
    const gearSize = 48 * sd;
    const gearX    = W - gearSize / 2 - 16 * sd;
    const gearY    = removeAdsBtnY;

    const gearGfx = this.add.graphics().setDepth(3);
    const drawGear = (color) => {
      gearGfx.clear();
      gearGfx.fillStyle(color, 0.85);
      gearGfx.fillRoundedRect(gearX - gearSize / 2, gearY - gearSize / 2, gearSize, gearSize, gearSize * 0.25);
    };
    drawGear(0x223355);

    this.add.text(gearX, gearY, '⚙️', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(4);

    const gearHit = this.add.rectangle(gearX, gearY, gearSize, gearSize)
      .setInteractive({ useHandCursor: true }).setDepth(4);
    gearHit.on('pointerover',  () => drawGear(0x3355aa));
    gearHit.on('pointerout',   () => drawGear(0x223355));
    gearHit.on('pointerdown',  () => { drawGear(0x112244); this.showSettings(sd, dpr); });

    // ── Rate Us button (below settings gear) ──────────────────
    const rateSize = 48 * sd;
    const rateX    = gearX;
    const rateY    = gearY + gearSize / 2 + rateSize / 2 + 10 * sd;

    const rateGfx = this.add.graphics().setDepth(3);
    const drawRate = (color) => {
      rateGfx.clear();
      rateGfx.fillStyle(color, 0.85);
      rateGfx.fillRoundedRect(rateX - rateSize / 2, rateY - rateSize / 2, rateSize, rateSize, rateSize * 0.25);
    };
    drawRate(0x553322);

    this.add.text(rateX, rateY, '⭐', {
      fontSize: `${26 * sd}px`, fontFamily: 'Arial, sans-serif',
    }).setOrigin(0.5).setDepth(4);

    const rateHit = this.add.rectangle(rateX, rateY, rateSize, rateSize)
      .setInteractive({ useHandCursor: true }).setDepth(4);
    rateHit.on('pointerover',  () => drawRate(0x775533));
    rateHit.on('pointerout',   () => drawRate(0x553322));
    rateHit.on('pointerdown',  () => { drawRate(0x331100); this.showRatingBanner(sd, dpr); });

    // ── High score text ───────────────────────────────────────────
    const scoreTextY = removeAdsBtnY - removeAdsBtnH * 0.5 - 20 * sd;
    this.scoreText = this.add.text(W / 2, scoreTextY, '\u{1F3C6}  Best: 0 animals  |  0 height', {
      fontSize: `${14 * sd}px`,
      fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5).setDepth(2);

    if (data && (data.highAnimals || data.highHeight)) {
      this.scoreText.setText(`\u{1F3C6}  Best: ${data.highAnimals} animals  |  ${data.highHeight} height`);
    } else {
      this.updateScores();
    }

    this.cameras.main.fadeIn(400, 0, 0, 0);

    showBanner();
  }

  // ── Cloud helper ──────────────────────────────────────────────────────────
  _makeCloud(x, y, sd, depth = 0) {
    const g = this.add.graphics().setDepth(depth);
    g.fillStyle(0xffffff, 0.88);
    g.fillEllipse(x,           y,          58 * sd, 28 * sd);
    g.fillEllipse(x - 20 * sd, y + 7 * sd, 38 * sd, 22 * sd);
    g.fillEllipse(x + 22 * sd, y + 5 * sd, 42 * sd, 24 * sd);
    g.fillStyle(0xf0faff, 0.50);
    g.fillEllipse(x - 5 * sd,  y - 7 * sd, 28 * sd, 14 * sd);
    return g;
  }

  /* ──────────────────── RATE US BANNER ────────────────────────── */
  showRatingBanner(sd, dpr) {
    if (this.ratingContainer) return;

    const W = this.scale.width;
    const H = this.scale.height;

    this.ratingContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(320 * sd, W * 0.85);
    const gap   = 48 * sd;

    const titleY    = H / 2 - gap * 1.8;
    const subY      = titleY   + gap * 0.8;
    const starsY    = subY     + gap * 1.1;
    const feedbackY = starsY   + gap * 0.7;
    const closeY    = feedbackY + gap * 1.0;

    const cardH       = closeY - titleY + gap;
    const cardCentreY = (titleY + closeY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive();
    const card = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const titleTxt = this.add.text(W / 2, titleY, '⭐  Rate Animal Stack!', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY, 'Tap a star to rate us on the Play Store!', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    const feedbackTxt = this.add.text(W / 2, feedbackY, '', {
      fontSize: `${13 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaffaa', align: 'center',
    }).setOrigin(0.5);

    const starSize = 38 * sd;
    const starGap  = 10 * sd;
    const totalStarsW = 5 * starSize + 4 * starGap;
    const startX = W / 2 - totalStarsW / 2 + starSize / 2;
    let selectedRating = 0;

    const stars = [];
    for (let i = 0; i < 5; i++) {
      const sx = startX + i * (starSize + starGap);
      const star = this.add.text(sx, starsY, '☆', {
        fontSize: `${starSize}px`, fontFamily: 'Arial, sans-serif',
        color: '#888888',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });

      star.on('pointerover', () => {
        for (let j = 0; j <= i; j++) {
          stars[j].setText('★').setColor('#ffdd00');
        }
        for (let j = i + 1; j < 5; j++) {
          if (j >= selectedRating) stars[j].setText('☆').setColor('#888888');
        }
      });

      star.on('pointerout', () => {
        for (let j = 0; j < 5; j++) {
          if (j < selectedRating) {
            stars[j].setText('★').setColor('#ffdd00');
          } else {
            stars[j].setText('☆').setColor('#888888');
          }
        }
      });

      star.on('pointerdown', () => {
        selectedRating = i + 1;
        for (let j = 0; j < 5; j++) {
          if (j < selectedRating) {
            stars[j].setText('★').setColor('#ffdd00');
          } else {
            stars[j].setText('☆').setColor('#888888');
          }
        }

        if (selectedRating >= 4) {
          feedbackTxt.setText('Thanks! Taking you to the Play Store...').setColor('#aaffaa');
          markNeverAsk();
          this.time.delayedCall(800, () => {
            openPlayStoreListing();
            if (this.ratingContainer) {
              this.ratingContainer.destroy(true);
              this.ratingContainer = null;
            }
          });
        } else {
          feedbackTxt.setText('Thanks for your feedback!').setColor('#ffaa44');
          this.time.delayedCall(1200, () => {
            if (this.ratingContainer) {
              this.ratingContainer.destroy(true);
              this.ratingContainer = null;
            }
          });
        }
      });

      stars.push(star);
    }

    const closeTxt = this.add.text(W / 2, closeY, 'Close', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeTxt.on('pointerover', () => closeTxt.setColor('#dddddd'));
    closeTxt.on('pointerout',  () => closeTxt.setColor('#aaaaaa'));
    closeTxt.on('pointerdown', () => {
      if (this.ratingContainer) {
        this.ratingContainer.destroy(true);
        this.ratingContainer = null;
      }
    });

    this.ratingContainer.add([
      overlay, card, titleTxt, subTxt,
      ...stars, feedbackTxt, closeTxt,
    ]);
  }

  /* ──────────────────── COIN BANNER ────────────────────────────── */
  showCoinBanner(sd, dpr) { /* identical to original — omitted only for brevity in the task description but included fully here */
    if (this.coinContainer) return;

    const W = this.scale.width;
    const H = this.scale.height;

    this.coinContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(320 * sd, W * 0.88);
    const gap   = 50 * sd;

    const titleY   = H / 2 - gap * 2.2;
    const subY     = titleY + gap * 0.9;
    const coinDispY = subY + gap * 1.0;
    const infoY    = coinDispY + gap * 0.85;
    const watchBtnY = infoY + gap * 1.1;
    const closeY   = watchBtnY + gap * 0.85;

    const cardH       = closeY - titleY + gap;
    const cardCentreY = (titleY + closeY) / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
      .setInteractive();
    const card = this.add.rectangle(W / 2, cardCentreY, cardW, cardH, 0x0a1628, 0.97)
      .setStrokeStyle(3 * dpr, 0xffdd00);

    const titleTxt = this.add.text(W / 2, titleY, 'Coin Shop', {
      fontSize: `${22 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00',
    }).setOrigin(0.5);

    const subTxt = this.add.text(W / 2, subY, 'Use coins to unlock new animals', {
      fontSize: `${14 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#cccccc', align: 'center',
    }).setOrigin(0.5);

    const coinIconSize = 24 * sd;
    const coinIconGfx = this.add.graphics();
    coinIconGfx.fillStyle(0xddaa00, 1);
    coinIconGfx.fillCircle(W / 2 - 40 * sd, coinDispY, coinIconSize * 0.5);
    coinIconGfx.fillStyle(0xffdd44, 1);
    coinIconGfx.fillCircle(W / 2 - 40 * sd, coinDispY, coinIconSize * 0.38);
    coinIconGfx.fillStyle(0xddaa00, 1);
    coinIconGfx.fillCircle(W / 2 - 40 * sd, coinDispY, coinIconSize * 0.15);

    const balanceTxt = this.add.text(W / 2 - 10 * sd, coinDispY, '0', {
      fontSize: `${28 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0, 0.5);

    getCoins().then(coins => {
      if (balanceTxt.active) balanceTxt.setText(`${coins}`);
    });

    const infoTxt = this.add.text(W / 2, infoY, '', {
      fontSize: `${12 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa', align: 'center',
    }).setOrigin(0.5);

    const watchBtnW = Math.min(260 * sd, W * 0.65);
    const watchBtnH = 56 * sd;
    const watchBtnR = watchBtnH / 2;

    const watchGfx = this.add.graphics();
    const watchBtnText = this.add.text(W / 2, watchBtnY, '', {
      fontSize: `${16 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    const watchHit = this.add.rectangle(W / 2, watchBtnY, watchBtnW, watchBtnH)
      .setDepth(0);

    const drawWatchBtn = (color) => {
      watchGfx.clear();
      watchGfx.fillStyle(color, 1);
      watchGfx.fillRoundedRect(W / 2 - watchBtnW / 2, watchBtnY - watchBtnH / 2, watchBtnW, watchBtnH, watchBtnR);
    };

    const updateAdState = async () => {
      const canWatch = await canWatchAdForCoins();
      const remaining = await getAdWatchesRemaining();
      infoTxt.setText(`${remaining}/${MAX_ADS_PER_DAY} free watches left today`);

      if (canWatch) {
        drawWatchBtn(0x44bb44);
        watchBtnText.setText(`+${COINS_PER_AD} Coins — Watch Ad`);
        watchHit.setInteractive({ useHandCursor: true });
        watchHit.off('pointerover').off('pointerout').off('pointerdown');
        watchHit.on('pointerover', () => drawWatchBtn(0x55dd55));
        watchHit.on('pointerout',  () => drawWatchBtn(0x44bb44));
        watchHit.on('pointerdown', () => {
          drawWatchBtn(0x338833);
          watchHit.removeInteractive();
          watchBtnText.setText('Loading...');

          showRewardedCoin(
            async () => {
              const { newBalance, adsRemaining } = await recordAdWatch();
              if (balanceTxt.active) balanceTxt.setText(`${newBalance}`);
              if (this.coinCounterText) this.coinCounterText.setText(`${newBalance}`);
              infoTxt.setText(`${adsRemaining}/${MAX_ADS_PER_DAY} free watches left today`);
              updateAdState();
            },
            () => {
              updateAdState();
            }
          );
        });
      } else {
        drawWatchBtn(0x555555);
        watchBtnText.setText('Come back tomorrow!');
        watchHit.removeInteractive();
      }
    };
    updateAdState();

    const closeTxt = this.add.text(W / 2, closeY, 'Close', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeTxt.on('pointerover', () => closeTxt.setColor('#dddddd'));
    closeTxt.on('pointerout',  () => closeTxt.setColor('#aaaaaa'));
    closeTxt.on('pointerdown', () => {
      if (this.coinContainer) {
        this.coinContainer.destroy(true);
        this.coinContainer = null;
      }
    });

    this.coinContainer.add([
      overlay, card, titleTxt, subTxt,
      coinIconGfx, balanceTxt, infoTxt,
      watchGfx, watchBtnText, watchHit,
      closeTxt,
    ]);
  }

  /* ──────────────────── ANIMAL SHOP ─────────────────────────── */
  async showAnimalShop(sd, dpr) {
    if (this.shopContainer) return;

    const W = this.scale.width;
    const H = this.scale.height;

    this.shopContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(340 * sd, W * 0.92);
    const cardH = Math.min(520 * sd, H * 0.85);
    const cardX = W / 2;
    const cardY = H / 2;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive();

    const card = this.add.graphics();
    card.fillStyle(0x0d1f2d, 0.97);
    card.fillRoundedRect(cardX - cardW / 2, cardY - cardH / 2, cardW, cardH, 16 * sd);
    card.lineStyle(3 * dpr, 0x55cc55, 1);
    card.strokeRoundedRect(cardX - cardW / 2, cardY - cardH / 2, cardW, cardH, 16 * sd);

    const titleTxt = this.add.text(cardX, cardY - cardH / 2 + 30 * sd, 'Animal Shop', {
      fontSize: `${22 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
      color: '#55ee55',
    }).setOrigin(0.5);

    const subTxt = this.add.text(cardX, cardY - cardH / 2 + 55 * sd, 'Unlock new animals for your game!', {
      fontSize: `${12 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaccaa',
    }).setOrigin(0.5);

    const coins = await getCoins();
    const coinTxt = this.add.text(cardX, cardY - cardH / 2 + 78 * sd, `\u{1F4B0} ${coins} coins`, {
      fontSize: `${16 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffdd00',
    }).setOrigin(0.5);

    const unlocked = await getUnlockedAnimals();
    const items = [];

    const displayNames = {
      cow: 'Cow', panda: 'Panda', pig: 'Pig',
      puppy: 'Puppy', horse: 'Horse', tiger2: 'Tiger',
    };

    const animalPrices = {
      tiger2: 100, cow: 500, pig: 700, panda: 1000, horse: 1500, puppy: 2000,
    };

    const cols = 3;
    const cellW = (cardW - 40 * sd) / cols;
    const cellH = 130 * sd;
    const gridTop = cardY - cardH / 2 + 100 * sd;
    const gridLeft = cardX - cardW / 2 + 20 * sd;

    const reopenShop = () => {
      if (this._previewContainer) {
        this._previewContainer.destroy(true);
        this._previewContainer = null;
      }
      if (this.shopContainer) {
        this.shopContainer.destroy(true);
        this.shopContainer = null;
      }
      this.showAnimalShop(sd, dpr);
    };

    const burstCelebration = (cx, cy) => {
      const colors = [0xffdd00, 0x55ee55, 0xff66aa, 0x44ccff, 0xffaa00, 0xffffff];
      const particleCount = 40;

      for (let p = 0; p < particleCount; p++) {
        const color = colors[Math.floor(Math.random() * colors.length)];
        const size = (3 + Math.random() * 5) * sd;
        const isCircle = Math.random() > 0.4;

        const particle = this.add.graphics().setDepth(15);
        if (isCircle) {
          particle.fillStyle(color, 1);
          particle.fillCircle(0, 0, size);
        } else {
          particle.fillStyle(color, 1);
          particle.fillRect(-size, -size * 0.4, size * 2, size * 0.8);
        }
        particle.setPosition(cx, cy);

        const angle = Math.random() * Math.PI * 2;
        const speed = (120 + Math.random() * 200) * sd;
        const dx = Math.cos(angle) * speed;
        const dy = Math.sin(angle) * speed - 80 * sd;
        const dur = 700 + Math.random() * 500;

        this.tweens.add({
          targets: particle,
          x: cx + dx, y: cy + dy + 120 * sd,
          alpha: 0, angle: Math.random() * 720 - 360,
          scaleX: 0.2, scaleY: 0.2,
          duration: dur, ease: 'Cubic.easeOut',
          onComplete: () => particle.destroy(),
        });
      }

      const starEmojis = ['⭐', '✨', '\u{1F31F}', '\u{1F389}'];
      for (let s = 0; s < 6; s++) {
        const emoji = starEmojis[Math.floor(Math.random() * starEmojis.length)];
        const star = this.add.text(cx, cy, emoji, {
          fontSize: `${(18 + Math.random() * 14) * sd}px`,
        }).setOrigin(0.5).setDepth(15);

        const angle = Math.random() * Math.PI * 2;
        const dist = (80 + Math.random() * 140) * sd;

        this.tweens.add({
          targets: star,
          x: cx + Math.cos(angle) * dist,
          y: cy + Math.sin(angle) * dist - 40 * sd,
          alpha: 0, scaleX: 1.5, scaleY: 1.5,
          duration: 800 + Math.random() * 400, ease: 'Cubic.easeOut',
          onComplete: () => star.destroy(),
        });
      }
    };

    const showPreview = (animal, isOwned) => {
      if (this._previewContainer) return;
      this._previewContainer = this.add.container(0, 0).setDepth(12);

      const pvOverlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.85)
        .setInteractive();

      const pvCardW = Math.min(280 * sd, W * 0.75);
      const pvCardH = 320 * sd;
      const pvCard = this.add.graphics();
      pvCard.fillStyle(0x1a8fff, 0.98);
      pvCard.fillRoundedRect(cardX - pvCardW / 2, cardY - pvCardH / 2, pvCardW, pvCardH, 16 * sd);
      pvCard.lineStyle(3 * dpr, isOwned ? 0x44aa44 : 0xffaa00, 1);
      pvCard.strokeRoundedRect(cardX - pvCardW / 2, cardY - pvCardH / 2, pvCardW, pvCardH, 16 * sd);

      const pvImgSize = 160 * sd;
      const pvImg = this.add.image(cardX, cardY - 40 * sd, animal)
        .setDisplaySize(pvImgSize, pvImgSize)
        .setAlpha(isOwned ? 1 : 0.7);

      const pvName = this.add.text(cardX, cardY + 55 * sd, displayNames[animal] || animal, {
        fontSize: `${20 * sd}px`, fontFamily: 'Chewy, Arial Rounded MT Bold, Arial, sans-serif',
        color: isOwned ? '#88ee88' : '#ffffff',
      }).setOrigin(0.5);

      const pvItems = [pvOverlay, pvCard, pvImg, pvName];

      if (isOwned) {
        const pvOwned = this.add.text(cardX, cardY + 85 * sd, '✓ Unlocked', {
          fontSize: `${14 * sd}px`, fontFamily: 'Arial, sans-serif',
          color: '#66bb66',
        }).setOrigin(0.5);
        pvItems.push(pvOwned);
      } else {
        const pvBtnW = 180 * sd;
        const pvBtnH = 44 * sd;
        const pvBtnY = cardY + 90 * sd;

        const pvBtnGfx = this.add.graphics();
        const drawPvBtn = (color) => {
          pvBtnGfx.clear();
          pvBtnGfx.fillStyle(color, 1);
          pvBtnGfx.fillRoundedRect(cardX - pvBtnW / 2, pvBtnY - pvBtnH / 2, pvBtnW, pvBtnH, pvBtnH / 2);
        };
        drawPvBtn(0x44aa44);

        const price = animalPrices[animal] || 1;
        const pvPriceTxt = this.add.text(cardX, pvBtnY, `\u{1F4B0} ${price} coins — Unlock`, {
          fontSize: `${14 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
          color: '#ffffff',
        }).setOrigin(0.5);

        const pvBtnHit = this.add.rectangle(cardX, pvBtnY, pvBtnW, pvBtnH)
          .setInteractive({ useHandCursor: true });
        pvBtnHit.on('pointerover', () => drawPvBtn(0x55cc55));
        pvBtnHit.on('pointerout',  () => drawPvBtn(0x44aa44));
        pvBtnHit.on('pointerdown', async () => {
          const success = await spendCoins(price);
          if (!success) {
            pvPriceTxt.setText('Not enough coins!');
            pvPriceTxt.setColor('#ff6666');
            this.time.delayedCall(1200, () => {
              if (pvPriceTxt.active) { pvPriceTxt.setText(`\u{1F4B0} ${price} coins — Unlock`); pvPriceTxt.setColor('#ffffff'); }
            });
            return;
          }
          await unlockAnimal(animal);
          const newBal = await getCoins();
          if (this.coinCounterText) this.coinCounterText.setText(`${newBal}`);

          pvImg.setAlpha(1);
          pvBtnHit.removeInteractive();
          drawPvBtn(0x336633);
          pvPriceTxt.setText('✓ Unlocked!');
          pvPriceTxt.setColor('#88ee88');
          pvName.setColor('#88ee88');

          burstCelebration(cardX, cardY - 40 * sd);

          this.time.delayedCall(1200, () => reopenShop());
        });
        pvItems.push(pvBtnGfx, pvPriceTxt, pvBtnHit);
      }

      const closePreview = () => {
        if (this._previewContainer) {
          this._previewContainer.destroy(true);
          this._previewContainer = null;
        }
      };
      const pvCloseY = cardY + pvCardH / 2 - 25 * sd;
      const pvClose = this.add.text(cardX, pvCloseY, 'Back', {
        fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif',
        color: '#aaaaaa',
      }).setOrigin(0.5).setInteractive({ useHandCursor: true });
      pvClose.on('pointerover', () => pvClose.setColor('#dddddd'));
      pvClose.on('pointerout',  () => pvClose.setColor('#aaaaaa'));
      pvClose.on('pointerdown', closePreview);
      pvOverlay.on('pointerdown', closePreview);
      pvItems.push(pvClose);
      this._previewContainer.add(pvItems);
    };

    UNLOCKABLE_ANIMALS.forEach((animal, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = gridLeft + col * cellW + cellW / 2;
      const cy = gridTop + row * cellH + cellH / 2;
      const isOwned = unlocked.includes(animal);

      const cellBg = this.add.graphics();
      cellBg.fillStyle(isOwned ? 0x1a3322 : 0x112233, 0.8);
      cellBg.fillRoundedRect(cx - cellW / 2 + 4 * sd, cy - cellH / 2 + 4 * sd, cellW - 8 * sd, cellH - 8 * sd, 10 * sd);
      cellBg.lineStyle(2 * dpr, isOwned ? 0x44aa44 : 0x334466, 1);
      cellBg.strokeRoundedRect(cx - cellW / 2 + 4 * sd, cy - cellH / 2 + 4 * sd, cellW - 8 * sd, cellH - 8 * sd, 10 * sd);
      items.push(cellBg);

      const imgSize = 55 * sd;
      const img = this.add.image(cx, cy - 12 * sd, animal)
        .setDisplaySize(imgSize, imgSize)
        .setAlpha(isOwned ? 1 : 0.4);
      items.push(img);

      if (!isOwned) {
        const lockR = 14 * sd;
        const lockGfx = this.add.graphics();
        lockGfx.fillStyle(0x000000, 0.7);
        lockGfx.fillCircle(cx, cy - 12 * sd, lockR);
        const bw = 10 * sd, bh = 8 * sd;
        lockGfx.fillStyle(0xffffff, 0.9);
        lockGfx.fillRoundedRect(cx - bw / 2, cy - 12 * sd - 1 * sd, bw, bh, 2 * sd);
        lockGfx.lineStyle(2.5 * sd, 0xffffff, 0.9);
        lockGfx.beginPath();
        lockGfx.arc(cx, cy - 12 * sd - 1 * sd, 4 * sd, Math.PI, 0, false);
        lockGfx.strokePath();
        items.push(lockGfx);
      }

      const nameTxt = this.add.text(cx, cy + 26 * sd, displayNames[animal] || animal, {
        fontSize: `${11 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
        color: isOwned ? '#88ee88' : '#cccccc',
      }).setOrigin(0.5);
      items.push(nameTxt);

      if (isOwned) {
        const ownedTxt = this.add.text(cx, cy + 42 * sd, '✓ Owned', {
          fontSize: `${10 * sd}px`, fontFamily: 'Arial, sans-serif',
          color: '#66bb66',
        }).setOrigin(0.5);
        items.push(ownedTxt);
      } else {
        const tapTxt = this.add.text(cx, cy + 44 * sd, `\u{1F4B0} ${animalPrices[animal]} — Tap`, {
          fontSize: `${9 * sd}px`, fontFamily: 'Arial, sans-serif',
          color: '#88aacc',
        }).setOrigin(0.5);
        items.push(tapTxt);
      }

      const cellHit = this.add.rectangle(cx, cy, cellW - 8 * sd, cellH - 8 * sd)
        .setInteractive({ useHandCursor: true });
      cellHit.on('pointerdown', () => showPreview(animal, isOwned));
      items.push(cellHit);
    });

    const totalRows = Math.ceil(UNLOCKABLE_ANIMALS.length / cols);
    const gridBottom = gridTop + totalRows * cellH;
    const hintY = gridBottom + 12 * sd;
    const hintTxt = this.add.text(cardX, hintY, 'Tap each animal to preview', {
      fontSize: `${11 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#77aa99', fontStyle: 'italic',
    }).setOrigin(0.5);

    const closeY = cardY + cardH / 2 - 25 * sd;
    const closeTxt = this.add.text(cardX, closeY, 'Close', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#aaaaaa',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    closeTxt.on('pointerover', () => closeTxt.setColor('#dddddd'));
    closeTxt.on('pointerout',  () => closeTxt.setColor('#aaaaaa'));
    closeTxt.on('pointerdown', () => {
      if (this._previewContainer) {
        this._previewContainer.destroy(true);
        this._previewContainer = null;
      }
      if (this.shopContainer) {
        this.shopContainer.destroy(true);
        this.shopContainer = null;
      }
    });

    this.shopContainer.add([overlay, card, titleTxt, subTxt, coinTxt, ...items, hintTxt, closeTxt]);
  }

  /* ──────────────────── REMOVE ADS CARD ──────────────────────── */
  showRemoveAdsCard(sd, dpr, applyRemoveAdsState) {
    // This is a no-op on web since getAdsRemoved() always returns true,
    // but we keep the method stub so calls don't crash.
    if (this.removeAdsContainer) return;
    // On web, ads are already removed, so this card should never appear.
    // If called, just apply the removed state immediately.
    applyRemoveAdsState(true);
  }

  /* ─────────────────────── SETTINGS MODAL ────────────────────── */
  showSettings(sd, dpr) {
    if (this.settingsContainer) return;

    const W = this.scale.width;
    const H = this.scale.height;

    this.settingsContainer = this.add.container(0, 0).setDepth(10);

    const cardW = Math.min(280 * sd, W * 0.78);
    const gap   = 52 * sd;
    const titleY     = H / 2 - gap * 1.6;
    const musicY     = titleY + gap;
    const sfxY       = musicY + gap * 0.85;
    const privacyY   = sfxY   + gap * 0.85;
    const noticeY    = privacyY + gap * 0.55;
    const closeY     = noticeY + gap * 0.75;
    const cardH      = closeY - titleY + gap * 0.9;
    const cardCentreY = (titleY + closeY) / 2;

    const cardTopY  = cardCentreY - cardH / 2;
    const cardBotY  = cardCentreY + cardH / 2;
    const cardR     = 18 * sd;

    const overlay = this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.55).setInteractive();

    const cardGfx = this.add.graphics();
    cardGfx.fillStyle(0x000000, 0.30);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2 + 4 * sd, cardTopY + 4 * sd, cardW, cardH, cardR);
    cardGfx.fillStyle(0xd0eeff, 0.97);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2, cardTopY, cardW, cardH, cardR);
    cardGfx.fillStyle(0x3a9ad9, 1);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2, cardTopY, cardW, 52 * sd, cardR);
    cardGfx.fillRect(W / 2 - cardW / 2, cardTopY + 26 * sd, cardW, 26 * sd);
    cardGfx.fillStyle(0x4caf50, 1);
    cardGfx.fillRoundedRect(W / 2 - cardW / 2, cardBotY - 52 * sd, cardW, 52 * sd, cardR);
    cardGfx.fillRect(W / 2 - cardW / 2, cardBotY - 52 * sd, cardW, 26 * sd);
    cardGfx.lineStyle(3 * dpr, 0x2288cc, 1);
    cardGfx.strokeRoundedRect(W / 2 - cardW / 2, cardTopY, cardW, cardH, cardR);

    const title = this.add.text(W / 2, cardTopY + 26 * sd, '⚙️  Settings', {
      fontSize: `${20 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#1a5fa8', strokeThickness: 3 * dpr,
    }).setOrigin(0.5);

    const toggleW = Math.min(220 * sd, cardW * 0.78);
    const toggleH = 42 * sd;
    const toggleR = toggleH / 2;

    const musicGfx = this.add.graphics();
    const drawMusic = (color) => {
      musicGfx.clear();
      musicGfx.fillStyle(color, 1);
      musicGfx.fillRoundedRect(W / 2 - toggleW / 2, musicY - toggleH / 2, toggleW, toggleH, toggleR);
    };
    drawMusic(0x1a5fa8);

    const musicTxt = this.add.text(W / 2, musicY,
      audioManager.isMuted ? '\u{1F507}  Music Off' : '\u{1F50A}  Music On', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#0a3a6a', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    const musicHit = this.add.rectangle(W / 2, musicY, toggleW, toggleH).setInteractive({ useHandCursor: true });
    musicHit.on('pointerover',  () => drawMusic(0x2277cc));
    musicHit.on('pointerout',   () => drawMusic(0x1a5fa8));
    musicHit.on('pointerdown',  () => {
      audioManager.ensureStarted();
      audioManager.setMuted(!audioManager.isMuted);
      musicTxt.setText(audioManager.isMuted ? '\u{1F507}  Music Off' : '\u{1F50A}  Music On');
      drawMusic(0x1a5fa8);
    });

    const sfxGfx = this.add.graphics();
    const drawSfx = (color) => {
      sfxGfx.clear();
      sfxGfx.fillStyle(color, 1);
      sfxGfx.fillRoundedRect(W / 2 - toggleW / 2, sfxY - toggleH / 2, toggleW, toggleH, toggleR);
    };
    drawSfx(0x1a5fa8);

    const sfxTxt = this.add.text(W / 2, sfxY,
      audioManager.isSfxMuted ? '\u{1F507}  Effects Off' : '\u{1F50A}  Effects On', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#0a3a6a', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    const sfxHit = this.add.rectangle(W / 2, sfxY, toggleW, toggleH).setInteractive({ useHandCursor: true });
    sfxHit.on('pointerover',  () => drawSfx(0x2277cc));
    sfxHit.on('pointerout',   () => drawSfx(0x1a5fa8));
    sfxHit.on('pointerdown',  () => {
      audioManager.ensureStarted();
      audioManager.setSfxMuted(!audioManager.isSfxMuted);
      sfxTxt.setText(audioManager.isSfxMuted ? '\u{1F507}  Effects Off' : '\u{1F50A}  Effects On');
      drawSfx(0x1a5fa8);
    });

    const privacyGfx = this.add.graphics();
    const drawPrivacy = (color) => {
      privacyGfx.clear();
      privacyGfx.fillStyle(color, 1);
      privacyGfx.fillRoundedRect(W / 2 - toggleW / 2, privacyY - toggleH / 2, toggleW, toggleH, toggleR);
    };
    drawPrivacy(0x1a5fa8);

    const privacyTxt = this.add.text(W / 2, privacyY, '\u{1F512}  Privacy Policy', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#0a3a6a', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    const privacyHit = this.add.rectangle(W / 2, privacyY, toggleW, toggleH).setInteractive({ useHandCursor: true });
    privacyHit.on('pointerover', () => drawPrivacy(0x2277cc));
    privacyHit.on('pointerout',  () => drawPrivacy(0x1a5fa8));
    privacyHit.on('pointerdown', () => {
      window.open('https://sites.google.com/view/seventwelveanimalstack/privacy-policy', '_blank');
    });

    const noticeTxt = this.add.text(W / 2, noticeY,
      'By using this app, you accept our Privacy Policy.', {
      fontSize: `${9 * sd}px`, fontFamily: 'Arial, sans-serif',
      color: '#667788', align: 'center',
      wordWrap: { width: cardW * 0.85 },
    }).setOrigin(0.5);

    const closeW   = Math.min(140 * sd, cardW * 0.5);
    const closeH   = 40 * sd;
    const closeGfx = this.add.graphics();
    const drawClose = (color) => {
      closeGfx.clear();
      closeGfx.fillStyle(color, 1);
      closeGfx.fillRoundedRect(W / 2 - closeW / 2, closeY - closeH / 2, closeW, closeH, closeH / 2);
    };
    drawClose(0x2a7a2a);
    const closeTxt = this.add.text(W / 2, closeY, '✕  Close', {
      fontSize: `${15 * sd}px`, fontFamily: 'Arial Rounded MT Bold, Arial, sans-serif',
      color: '#ffffff', stroke: '#000000', strokeThickness: 2 * dpr,
    }).setOrigin(0.5);

    const closeHit = this.add.rectangle(W / 2, closeY, closeW, closeH).setInteractive({ useHandCursor: true });
    closeHit.on('pointerover',  () => drawClose(0x3a9a3a));
    closeHit.on('pointerout',   () => drawClose(0x2a7a2a));
    closeHit.on('pointerdown',  () => {
      this.settingsContainer.destroy(true);
      this.settingsContainer = null;
    });

    this.settingsContainer.add([
      overlay, cardGfx, title,
      musicGfx, musicTxt, musicHit,
      sfxGfx, sfxTxt, sfxHit,
      privacyGfx, privacyTxt, privacyHit, noticeTxt,
      closeGfx, closeTxt, closeHit,
    ]);
  }

  updateScores() {
    Promise.all([
      Preferences.get({ key: 'highAnimals' }),
      Preferences.get({ key: 'highHeight' }),
    ]).then(([a, h]) => {
      const highAnimals = parseInt(a.value) || 0;
      const highHeight  = parseInt(h.value) || 0;
      if (this.scoreText) {
        this.scoreText.setText(`\u{1F3C6}  Best: ${highAnimals} animals  |  ${highHeight} height`);
      }
    });
  }
}
